import { useScenario } from 'modules/project/providers/scenario';
import {
  BackfillRecipeDto,
  FillType,
  PourDto,
  PourNoteRoutePointsDto,
  RheologyDataDto,
  useMineModelQuery,
  useMineOverviewQuery,
  useProjectQuery,
  useRecipesQuery
} from 'providers/api';
import React from 'react';
import { createProjectNumberFormatter } from 'utils';
import buildRouteHglImageContext from '../ProjectAreaPage/ProjectStopesPage/HydraulicGradeLine/hglRouteImage';
import { HglImageContext, renderHglImageDataUrl } from '../ProjectAreaPage/ProjectStopesPage/HydraulicGradeLine/hglImageExport';
import { useModelState } from '../ProjectAreaPage/ProjectStopesPage/hooks';
import { buildPourRouteSolution, PourRouteSolution, toPourRoutePoints } from './stopeRoutePoints';

interface UseStopeRoutePointsParams {
  projectId: string;
  stopeId: string;
  pours: PourDto[];
  fillType: FillType;
  enabled: boolean;
  captureHglImages?: boolean;
}

export type RoutePointsByPour = Record<string, PourNoteRoutePointsDto[]>;

export interface HglChart {
  pourIds: string[];
  imageBase64: string;
}

const renderHglImages = async (
  solutionsByRecipe: Map<string, PourRouteSolution>,
  build: (solution: PourRouteSolution, recipeId: string) => HglImageContext | undefined,
): Promise<Map<string, string>> => {
  const rendered = new Map<string, string>();

  await Promise.all(Array.from(solutionsByRecipe.entries()).map(async ([recipeId, solution]) => {
    try {
      const context = build(solution, recipeId);
      const dataUrl = context ? await renderHglImageDataUrl(context) : undefined;
      if (dataUrl) rendered.set(recipeId, dataUrl);
    } catch {
      // A failed render must not block note generation — the note omits that section.
    }
  }));

  return rendered;
};

// Loads the reticulation model headlessly (no canvas) and computes, for every recipe pour, the
// route points for that pour's recipe-specific route + pressures — the same computation the
// single-pour flow performs, but for all pours at once so the stope note can carry route-derived
// values (pipeline length, static difference, time to stope, …).
const useStopeRoutePoints = ({
  projectId, stopeId, pours, fillType, enabled, captureHglImages = false,
}: UseStopeRoutePointsParams) => {
  const { activeScenarioId } = useScenario();

  const { data: project } = useProjectQuery(projectId, { enabled });
  const { data: mineModel } = useMineModelQuery({ mineModelId: projectId }, { enabled });
  const { data: mineOverview } = useMineOverviewQuery({ mineModelId: projectId }, { enabled });
  const { data: recipes } = useRecipesQuery(
    { projectId, stopeId, scenarioId: activeScenarioId ?? undefined },
    { enabled: enabled && !!stopeId },
  );

  const { model, initModelState, loaded } = useModelState();
  const initialisedRef = React.useRef(false);
  const computedKeyRef = React.useRef('');
  // Discards stale renders by token, not by effect cleanup: a cleanup would cancel the in-flight
  // render on a re-run the key guard then skips, leaving the images permanently unset.
  const renderTokenRef = React.useRef(0);

  const [routePointsByPour, setRoutePointsByPour] = React.useState<RoutePointsByPour>({});
  const [hglCharts, setHglCharts] = React.useState<HglChart[]>([]);
  const [isReady, setIsReady] = React.useState(false);

  // Build the model once the supporting data is available.
  React.useEffect(() => {
    if (!enabled || initialisedRef.current) return;
    if (project && mineModel && mineOverview) {
      initModelState(project, mineModel, mineOverview);
      initialisedRef.current = true;
    }
  }, [enabled, project, mineModel, mineOverview, initModelState]);

  // Once the model is loaded, compute route points for every recipe pour. Guarded by an input
  // key so it runs once per (stope, pours, recipes) set rather than on every model state tick.
  React.useEffect(() => {
    if (!enabled || !loaded() || !recipes) return;

    // `model.rheology` is populated by a separate query that only starts after the model is
    // initialised, so on a cold page refresh the first compute can run before it lands — yielding an
    // empty result. Including its count in the key lets the computation re-run once rheology arrives
    // instead of permanently caching that empty result (which surfaced as a stuck "route data
    // unavailable" warning on refresh).
    const rheologyCount = model.rheology?.length ?? 0;
    const key = `${stopeId}|${pours.map((p) => p.entityId).join(',')}|${recipes.length}|${rheologyCount}|${fillType}|${captureHglImages}`;
    if (computedKeyRef.current === key) return;

    computedKeyRef.current = key;
    renderTokenRef.current += 1;
    const token = renderTokenRef.current;

    const allStopes = Object.values(model.stopes ?? {}).flat() as any[];
    const stope = allStopes.find((s) => s?.data?.stopeId === stopeId);

    const result: RoutePointsByPour = {};
    const solutionsByRecipe = new Map<string, PourRouteSolution>();
    const recipesById = new Map<string, BackfillRecipeDto>();
    const rheologyByRecipe = new Map<string, RheologyDataDto>();
    const pourIdsByRecipe = new Map<string, string[]>();

    if (stope) {
      pours.filter((pour) => !pour.isRockFill && !!pour.recipeId).forEach((pour) => {
        const recipe = recipes.find((r) => r.entityId === pour.recipeId);
        if (!recipe) return;
        const rheologyDataSet = model.rheology?.find((dataset) => dataset.rheologyDataSetId === recipe.rheologyDataSetId);
        if (!rheologyDataSet) return;

        const solution = solutionsByRecipe.get(recipe.entityId)
          ?? buildPourRouteSolution(model, stope, recipe, rheologyDataSet, fillType);
        if (!solution) return;

        solutionsByRecipe.set(recipe.entityId, solution);
        recipesById.set(recipe.entityId, recipe);
        rheologyByRecipe.set(recipe.entityId, rheologyDataSet);
        pourIdsByRecipe.set(recipe.entityId, [...(pourIdsByRecipe.get(recipe.entityId) ?? []), pour.entityId]);

        const points = toPourRoutePoints(solution);
        if (points.length > 0) {
          result[pour.entityId] = points;
        }
      });
    }

    // Ready means the computation has been attempted (model loaded + recipes available), even when no
    // routes resolved — so callers can reliably gate generation on it without blocking forever.
    setRoutePointsByPour(result);

    if (!captureHglImages || solutionsByRecipe.size === 0) {
      setHglCharts([]);
      setIsReady(true);
      return;
    }

    const stopeName = stope?.data?.stopeName as string | undefined;
    const formatNumber = createProjectNumberFormatter(project?.decimalSeparator, project?.thousandsSeparator);

    renderHglImages(solutionsByRecipe, (solution, recipeId) => {
      const recipe = recipesById.get(recipeId);
      const showMinMax = Boolean(model.project?.showMinMaxOnGraph);
      return buildRouteHglImageContext({
        route: solution.route,
        recipeState: solution.recipeState,
        rheologyDataset: rheologyByRecipe.get(recipeId),
        unitSystemPreference: model.project.unitSystemPreference,
        customUnitFields: model.project.customUnitFields,
        formatNumber,
        slackFlow: showMinMax && solution.slackSlurryDensity !== undefined
          ? { pressuresByPointId: solution.slackPressures, slurryDensity: solution.slackSlurryDensity }
          : undefined,
        maxPressure: showMinMax && solution.maxSlurryDensity !== undefined
          ? { pressuresByPointId: solution.maxPressures, slurryDensity: solution.maxSlurryDensity }
          : undefined,
        pipeRatingOffsetPercent: model.project?.showPipeRatingOffsetOnGraph
          ? recipe?.nominalParameters?.pipeRatingOffsetPercent ?? undefined
          : undefined,
        stopeName,
      });
    }).then((byRecipe) => {
      if (renderTokenRef.current !== token) return;
      // Ordered by solve, not by render completion, so the note's sections follow pour order.
      setHglCharts(Array.from(solutionsByRecipe.keys())
        .map((recipeId) => ({ pourIds: pourIdsByRecipe.get(recipeId) ?? [], imageBase64: byRecipe.get(recipeId) }))
        .filter((chart): chart is HglChart => chart.imageBase64 !== undefined));
      setIsReady(true);
    });
  }, [enabled, loaded, model, recipes, pours, stopeId, fillType, captureHglImages, project]);

  return { routePointsByPour, hglCharts, isReady };
};

export default useStopeRoutePoints;
